import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { UsersThree, Plus, Trash, ClockCounterClockwise, Eye, PencilSimple, Briefcase, LinkSimple, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERM_COLORS = {
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  editor: "bg-blue-100 text-blue-700 border-blue-200",
  viewer: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function TeamManagement() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);

  // Forms
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", password: "mudar123", position_id: "", role: "editor", employee_modules: [] });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkRole, setLinkRole] = useState("editor");
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const { data } = await axios.post(`${API}/team/invite-link`, { role: linkRole });
      setGeneratedLink(`${window.location.origin}/join/${data.token}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("tm_link_error"));
    }
    setGeneratingLink(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success(t("tm_link_copied"));
    } catch {
      toast.info(t("tm_copy_manual"));
    }
  };
  const [editForm, setEditForm] = useState({ id: "", name: "", position_id: "", employee_modules: [] });
  const [posForm, setPosForm] = useState({ id: null, name: "", permission_level: "viewer" });

  const isAdmin = user && !user.is_employee && user.role === "admin";
  const isEmployee = user?.is_employee;

  const fetchData = useCallback(async () => {
    try {
      if (isAdmin) {
        const [teamRes, logRes, posRes, modRes] = await Promise.all([
          axios.get(`${API}/team`),
          axios.get(`${API}/audit-log`),
          axios.get(`${API}/team/positions`),
          axios.get(`${API}/modules`),
        ]);
        setMembers(teamRes.data);
        setAuditLog(logRes.data);
        setPositions(posRes.data);
        const parentMods = modRes.data.modules.filter(m => m.enabled);
        setModules(parentMods);
      }
    } catch (e) {
      console.warn("Team data unavailable (viewer sem permissão):", e?.response?.status || e?.message);
    }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ==================== INVITE ====================
  const handleInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) { toast.error(t("tm_name_required")); return; }
    try {
      const payload = {
        name: inviteForm.name,
        email: inviteForm.email,
        password: inviteForm.password,
        position_id: (inviteForm.position_id && inviteForm.position_id !== "none") ? inviteForm.position_id : null,
        role: inviteForm.role,
        employee_modules: inviteForm.employee_modules.length > 0 ? inviteForm.employee_modules : null,
      };
      const { data } = await axios.post(`${API}/team/invite`, payload);
      toast.success(data?.message || t("tm_invited_success"));
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", password: "mudar123", position_id: "", role: "editor", employee_modules: [] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== EDIT EMPLOYEE ====================
  const openEdit = (member) => {
    setEditForm({
      id: member.id,
      name: member.name,
      position_id: member.position_id || "",
      employee_modules: member.employee_modules || modules.map(m => m.id),
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`${API}/team/${editForm.id}`, {
        name: editForm.name,
        position_id: (editForm.position_id && editForm.position_id !== "none") ? editForm.position_id : null,
        employee_modules: editForm.employee_modules,
      });
      toast.success(t("tm_member_updated"));
      setEditOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== REMOVE ====================
  const handleRemove = async (userId, name) => {
    if (!window.confirm(t("tm_remove_confirm").replace("{name}", name))) return;
    try {
      await axios.delete(`${API}/team/${userId}`);
      toast.success(t("tm_member_removed"));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== POSITIONS ====================
  const openNewPosition = () => { setPosForm({ id: null, name: "", permission_level: "viewer" }); setPositionDialogOpen(true); };
  const openEditPosition = (pos) => { setPosForm({ id: pos.id, name: pos.name, permission_level: pos.permission_level }); setPositionDialogOpen(true); };

  const handleSavePosition = async () => {
    if (!posForm.name.trim()) { toast.error(t("tm_name_required")); return; }
    try {
      if (posForm.id) {
        await axios.put(`${API}/team/positions/${posForm.id}`, { name: posForm.name, permission_level: posForm.permission_level });
        toast.success(t("tm_position_updated"));
      } else {
        await axios.post(`${API}/team/positions`, { name: posForm.name, permission_level: posForm.permission_level });
        toast.success(t("tm_position_created"));
      }
      setPositionDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleDeletePosition = async (pos) => {
    if (!window.confirm(t("tm_delete_position_confirm").replace("{name}", pos.name))) return;
    try {
      await axios.delete(`${API}/team/positions/${pos.id}`);
      toast.success(t("tm_position_deleted"));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== MODULE TOGGLE HELPER ====================
  const toggleModule = (modId, form, setForm) => {
    const current = form.employee_modules || [];
    setForm({
      ...form,
      employee_modules: current.includes(modId) ? current.filter(m => m !== modId) : [...current, modId],
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  // Employee readonly view
  if (isEmployee) {
    return (
      <div className="space-y-6" data-testid="team-page">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("tm_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("tm_subtitle")}</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Eye size={48} className="text-zinc-300 mb-4" />
            <p className="text-zinc-600 font-medium text-lg">{t("tm_readonly_notice")}</p>
            <p className="text-zinc-400 text-sm mt-2">{t("tm_viewer")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("tm_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("tm_subtitle")}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button onClick={() => { setLinkOpen(true); setGeneratedLink(""); }} variant="outline" className="border-zinc-300" data-testid="invite-link-btn">
              <LinkSimple size={16} className="mr-2" /> {t("tm_invite_link")}
            </Button>
            <Button onClick={() => setInviteOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="invite-member-btn">
              <Plus size={16} className="mr-2" /> {t("tm_invite")}
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="equipe">
        <TabsList className="grid w-full grid-cols-3 text-xs md:text-sm">
          <TabsTrigger value="equipe" data-testid="tab-team"><UsersThree size={16} className="mr-2" /> {t("tm_team_tab")}</TabsTrigger>
          <TabsTrigger value="cargos" data-testid="tab-positions"><Briefcase size={16} className="mr-2" /> {t("tm_positions_tab")}</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="tab-audit"><ClockCounterClockwise size={16} className="mr-2" /> {t("tm_audit")}</TabsTrigger>
        </TabsList>

        {/* ==================== EQUIPE TAB ==================== */}
        <TabsContent value="equipe" className="mt-4">
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow className="bg-zinc-50">
                <TableHead className="text-xs font-semibold">{t("name")}</TableHead>
                <TableHead className="text-xs font-semibold">{t("email")}</TableHead>
                <TableHead className="text-xs font-semibold text-center">{t("tm_position")}</TableHead>
                <TableHead className="text-xs font-semibold text-center">{t("tm_permission_level")}</TableHead>
                <TableHead className="text-xs font-semibold text-center">{t("tm_modules")}</TableHead>
                {isAdmin && <TableHead className="text-xs font-semibold text-center">{t("tm_action")}</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} data-testid={`member-${m.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${m.is_employee ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>
                          {m.is_employee ? t("tm_employee_badge") : t("tm_owner_badge")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">{m.email}</TableCell>
                    <TableCell className="text-center text-sm">
                      {m.position_name || <span className="text-zinc-300">—</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${PERM_COLORS[m.role] || PERM_COLORS.viewer}`}>
                        {m.role === "admin" ? t("tm_admin") : m.role === "editor" ? t("tm_editor") : t("tm_viewer")}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-xs text-zinc-500">
                      {m.is_employee ? (m.employee_modules ? `${m.employee_modules.length}/${modules.length}` : t("tm_all_modules")) : "—"}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-center">
                        {m.is_employee && (
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-zinc-900" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>
                              <PencilSimple size={14} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => handleRemove(m.id, m.name)} data-testid={`remove-member-${m.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          </div>
        </TabsContent>

        {/* ==================== CARGOS TAB ==================== */}
        <TabsContent value="cargos" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={openNewPosition} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="new-position-btn">
              <Plus size={16} className="mr-2" /> {t("tm_new_position")}
            </Button>
          </div>
          {positions.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <Briefcase size={48} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-500">{t("tm_no_positions")}</p>
            </CardContent></Card>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow className="bg-zinc-50">
                  <TableHead className="text-xs font-semibold">{t("tm_position_name")}</TableHead>
                  <TableHead className="text-xs font-semibold text-center">{t("tm_permission_level")}</TableHead>
                  <TableHead className="text-xs font-semibold text-center">{t("tm_action")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {positions.map((pos) => (
                    <TableRow key={pos.id} data-testid={`position-${pos.id}`}>
                      <TableCell className="font-medium">{pos.name}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${PERM_COLORS[pos.permission_level] || PERM_COLORS.viewer}`}>
                          {pos.permission_level === "admin" ? t("tm_admin") : pos.permission_level === "editor" ? t("tm_editor") : t("tm_viewer")}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-zinc-900" onClick={() => openEditPosition(pos)} data-testid={`edit-position-${pos.id}`}>
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => handleDeletePosition(pos)} data-testid={`delete-position-${pos.id}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            </div>
          )}
        </TabsContent>

        {/* ==================== AUDITORIA TAB ==================== */}
        <TabsContent value="auditoria" className="mt-4">
          {auditLog.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <ClockCounterClockwise size={48} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-500">{t("tm_no_audit")}</p>
            </CardContent></Card>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow className="bg-zinc-50">
                  <TableHead className="text-xs font-semibold">{t("tm_datetime")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("tm_user")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("tm_action")}</TableHead>
                  <TableHead className="text-xs font-semibold">{t("tm_details")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {auditLog.slice(0, 50).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs text-zinc-500">{new Date(log.timestamp).toLocaleString("pt-BR")}</TableCell>
                      <TableCell className="text-sm font-medium">{log.user_name}</TableCell>
                      <TableCell className="text-sm">{log.action}</TableCell>
                      <TableCell className="text-sm text-zinc-500 max-w-[200px] truncate">{log.details}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== INVITE DIALOG ==================== */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md" data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle>{t("tm_invite_link")}</DialogTitle>
            <DialogDescription>
              {t("tm_invite_link_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_access_level")}</Label>
              <Select value={linkRole} onValueChange={setLinkRole}>
                <SelectTrigger className="mt-1.5" data-testid="invite-link-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">{t("tm_role_editor_desc")}</SelectItem>
                  <SelectItem value="viewer">{t("tm_role_viewer_desc")}</SelectItem>
                  <SelectItem value="admin">{t("tm_role_admin_desc")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {generatedLink ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_link_label")}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={generatedLink} className="text-xs" data-testid="generated-invite-link" onFocus={(e) => e.target.select()} />
                  <Button variant="outline" size="icon" onClick={handleCopyLink} data-testid="copy-invite-link">
                    <Copy size={16} />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">{t("tm_link_note")}</p>
              </div>
            ) : (
              <Button onClick={handleGenerateLink} disabled={generatingLink} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="generate-invite-link-btn">
                <LinkSimple size={16} className="mr-2" /> {generatingLink ? t("tm_generating") : t("tm_generate_link")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("tm_invite")}</DialogTitle>
            <DialogDescription className="sr-only">{t("tm_invite")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("name")} *</Label>
              <Input value={inviteForm.name} onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})} className="mt-1.5" data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("email")} *</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})} className="mt-1.5" data-testid="invite-email" />
              <p className="text-xs text-zinc-500 mt-1">{t("tm_email_hint")}</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_access_level")}</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({...inviteForm, role: v})}>
                <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">{t("tm_role_editor_desc")}</SelectItem>
                  <SelectItem value="viewer">{t("tm_role_viewer_desc")}</SelectItem>
                  <SelectItem value="admin">{t("tm_role_admin_desc")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">{t("tm_role_hint")}</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_position")}</Label>
              <Select value={inviteForm.position_id} onValueChange={(v) => setInviteForm({...inviteForm, position_id: v})}>
                <SelectTrigger className="mt-1.5" data-testid="invite-position"><SelectValue placeholder={t("tm_no_position")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("tm_no_position")}</SelectItem>
                  {positions.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.permission_level === "admin" ? t("tm_admin") : p.permission_level === "editor" ? t("tm_editor") : t("tm_viewer")})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_modules")}</Label>
              <p className="text-xs text-zinc-400 mt-0.5 mb-2">{t("tm_select_modules")}</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-zinc-100 rounded p-2">
                {modules.map(mod => (
                  <label key={mod.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={inviteForm.employee_modules.includes(mod.id)}
                      onCheckedChange={() => toggleModule(mod.id, inviteForm, setInviteForm)}
                      data-testid={`invite-mod-${mod.id}`}
                    />
                    {mod.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_initial_password")}</Label>
              <Input value={inviteForm.password} onChange={(e) => setInviteForm({...inviteForm, password: e.target.value})} className="mt-1.5" />
              <p className="text-xs text-zinc-500 mt-1">{t("tm_change_first_login")}</p>
            </div>
            <Button onClick={handleInvite} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="confirm-invite-btn">
              {t("tm_invite")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT EMPLOYEE DIALOG ==================== */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("tm_edit_member")}</DialogTitle>
            <DialogDescription className="sr-only">{t("tm_edit_member")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("name")}</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1.5" data-testid="edit-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_position")}</Label>
              <Select value={editForm.position_id} onValueChange={(v) => setEditForm({...editForm, position_id: v})}>
                <SelectTrigger className="mt-1.5" data-testid="edit-position"><SelectValue placeholder={t("tm_no_position")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("tm_no_position")}</SelectItem>
                  {positions.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.permission_level === "admin" ? t("tm_admin") : p.permission_level === "editor" ? t("tm_editor") : t("tm_viewer")})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_modules")}</Label>
              <p className="text-xs text-zinc-400 mt-0.5 mb-2">{t("tm_select_modules")}</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-zinc-100 rounded p-2">
                {modules.map(mod => (
                  <label key={mod.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={(editForm.employee_modules || []).includes(mod.id)}
                      onCheckedChange={() => toggleModule(mod.id, editForm, setEditForm)}
                      data-testid={`edit-mod-${mod.id}`}
                    />
                    {mod.name}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={handleEditSave} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-edit-btn">
              {t("tm_save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== POSITION DIALOG ==================== */}
      <Dialog open={positionDialogOpen} onOpenChange={setPositionDialogOpen}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>{posForm.id ? t("tm_position") : t("tm_new_position")}</DialogTitle>
            <DialogDescription className="sr-only">{t("tm_new_position")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_position_name")} *</Label>
              <Input value={posForm.name} onChange={(e) => setPosForm({...posForm, name: e.target.value})} className="mt-1.5" data-testid="position-name" placeholder={t("tm_position_example")} />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("tm_permission_level")}</Label>
              <Select value={posForm.permission_level} onValueChange={(v) => setPosForm({...posForm, permission_level: v})}>
                <SelectTrigger className="mt-1.5" data-testid="position-perm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("tm_admin")}</SelectItem>
                  <SelectItem value="editor">{t("tm_editor")}</SelectItem>
                  <SelectItem value="viewer">{t("tm_viewer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSavePosition} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-position-btn">
              {t("tm_save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
